import React, { useState, useCallback, useEffect, useMemo } from "react";

import {
  InfoPanel,
  RadioButton,
  useStepperDispatch,
  ColumnSortEnum,
  Table,
  TableMetadata,
  useTable,
  Typography,
  IconWrapper,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as InstitutionIcon } from "@king-ict/eupisi-ui/components/Icon/Institution.svg";
import { useValidation } from "@king-ict/eupisi-ui/hooks";
import { getInstitutionDisplayNameAndTown } from "@king-ict/eupisi-ui/utils";
import { unwrapResult } from "@reduxjs/toolkit";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { searchInstitutions } from "../../../api/kindergartenApi";
import { RootState, useTypedSelector } from "../../../app/rootReducer";
import { useAppDispatch } from "../../../app/store";
import InstitutionDetails from "../../../models/Institution/InstitutionDetails";
import InstitutionDetailsValidationSchema from "../../../models/Institution/InstitutionDetailsValidationSchema";
import { InstitutionSearchItem } from "../../../models/Institution/InstitutionSearch";
import { InstitutionFilters } from "../../../models/Institution/InstitutionSearch";
import InstitutionSortEnum from "../../../models/Institution/InstitutionSortEnum";
import RequestStatusEnum from "../../../models/Request/RequestStatusEnum";
import PagedResult from "../../../models/Shared/PagedResult";
import { getInstitutionDetalis } from "../../../slices/institutionSlice";
import {
  updateRequestInstitutionPrograms,
  updateSelectedInstitution,
  updateRequestType,
} from "../../../slices/requestSlice";
import { InstitutionDetailsModal } from "../../Institutions/InstitutionDetailsModal";
import { InstitutionSearchFilters } from "../../Institutions/InstitutionsFilters";
import style from "./InstitutionDataStep.module.scss";

export default function InstitutionDataStep() {
  const requestStatusId = useTypedSelector(
    (state) => state.request.request.status.id
  );

  const currentChildOib = useSelector(
    (state: RootState) => state.request.request.child.oib
  );

  const [detailsModalOpen, setDetailsModalOpen] = React.useState(false);

  const selectedInstitutionState = useSelector(
    (state: RootState) => state.request.request.selectedInstitution
  );

  const preselectedInstitution = useSelector(
    (state: RootState) =>
      state.request.request.selectedPedagogicalYearInstitutionId
  );

  const requestType = useSelector(
    (state: RootState) => state.request.request.requestTypeId
  );

  const [filters, setFilters] = useState<InstitutionFilters>({
    childOib: currentChildOib,
    requestTypeId: requestType,
  } as InstitutionFilters);

  const [selectedInstitution, setSelectedInstitution] = useState<
    InstitutionDetails | undefined
  >(selectedInstitutionState);

  const [institutions, setInstitutions] = useState<
    PagedResult<InstitutionSearchItem>
  >(({
    rowCount: 0,
    results: [],
  } as unknown) as PagedResult<InstitutionSearchItem>);

  const [
    institutionDetails,
    setInstitutionDetails,
  ] = useState<InstitutionDetails>({} as InstitutionDetails);

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [errors, doValidations] = useValidation({
    data: selectedInstitution!,
    validationSchema: InstitutionDetailsValidationSchema,
  });

  useStepperDispatch({
    type: "next",
    payload: () => {
      return handleStep();
    },
  });

  useStepperDispatch({
    type: "previous",
    payload: () => {
      return handleStep();
    },
  });

  const handleOpenDetails = useCallback(
    (item: InstitutionSearchItem) => {
      dispatch(getInstitutionDetalis(item.pedagogicalYearInstitutionId))
        .then(unwrapResult)
        .then((res) => {
          setInstitutionDetails(res);
        });
      setDetailsModalOpen(true);
    },
    [dispatch]
  );

  const handleOpenSelectedDetails = () => {
    if (selectedInstitution) {
      setInstitutionDetails(selectedInstitution);
      setDetailsModalOpen(true);
    }
  };

  const handlerCloseDetails = () => {
    setDetailsModalOpen(false);
    setInstitutionDetails({} as InstitutionDetails);
  };

  const handleInstitutionChoice = useCallback(
    (e: any) => {
      const { value } = e.target;
      dispatch(getInstitutionDetalis(+value))
        .then(unwrapResult)
        .then((res) => {
          setSelectedInstitution(res);
        });
    },
    [dispatch]
  );

  const isItemDisabled = useCallback(
    (item: InstitutionSearchItem) => {
      return (
        item.isDisabled &&
        preselectedInstitution !== item.pedagogicalYearInstitutionId
      );
    },
    [preselectedInstitution]
  );

  const institutionTableMetadata = useMemo(
    () =>
      ({
        rowKey: "pedagogicalYearInstitutionId",
        rowToolTip: (d) => {
          return isItemDisabled(d)
            ? {
                content: !d.isGroup
                  ? t("institutionDataStep.institutionAlreadyChosen")
                  : t("institutionDataStep.institutionAlreadyChosenOnGroup"),
                placement: "left",
              }
            : undefined;
        },
        columns: [
          {
            columnKey: "institutionProgramDetailId",
            header: "",
            cellClassName: `${style.tableRadioColumn}`,
            accessor: (d) => (
              <RadioButton
                name="tableRadio"
                value={d.pedagogicalYearInstitutionId}
                checked={
                  selectedInstitution &&
                  selectedInstitution.pedagogicalYearInstitutionId ===
                    d.pedagogicalYearInstitutionId
                }
                onChange={
                  isItemDisabled(d) ? () => {} : handleInstitutionChoice
                }
                ariaLabel={t("accessibility.selectedRow")}
              />
            ),
          },
          {
            columnKey: "institutionColumn",
            header: t("common.institution"),
            cellClassName: `${style.tableNameColumn}`,
            headerClassName: `${style.tableNameColumn}`,
            onCellClick: handleOpenDetails,
            accessor: (item) =>
              getInstitutionDisplayNameAndTown(item.name, item.town),
            sortBy: InstitutionSortEnum.Institution,
          },
          {
            columnKey: "townColumn",
            header: t("institutionSearchPage.town"),
            onCellClick: handleOpenDetails,
            accessor: "municipality",
            sortBy: InstitutionSortEnum.Municipality,
          },
          {
            columnKey: "countyColumn",
            header: t("common.county"),
            onCellClick: handleOpenDetails,
            accessor: "county",
            sortBy: InstitutionSortEnum.County,
          },
          {
            columnKey: "founderTypeColumn",
            header: t("institutionSearchPage.founderType"),
            onCellClick: handleOpenDetails,
            accessor: "founderType",
            sortBy: InstitutionSortEnum.FounderType,
          },
        ],
      } as TableMetadata<InstitutionSearchItem>),
    [
      t,
      handleOpenDetails,
      isItemDisabled,
      selectedInstitution,
      handleInstitutionChoice,
    ]
  );

  const [
    tableProps,
    tableState,
    setTableFilters,
  ] = useTable<InstitutionFilters>({
    metadata: institutionTableMetadata,
    data: institutions.results,
    pagination: true,
    totalItems: institutions.rowCount,
    defaultSort: {
      sortBy: InstitutionSortEnum.Institution,
      order: ColumnSortEnum.ASC,
    },
  });

  useEffect(() => {
    (async () => {
      const res = await searchInstitutions({
        ...tableState,
        childOib: currentChildOib,
        requestTypeId: requestType,
        preselectedInstitution: preselectedInstitution,
      });
      setInstitutions(res);
    })();
  }, [currentChildOib, requestType, dispatch, tableState]);

  const handleSearch = () => {
    setTableFilters({ ...filters, childOib: currentChildOib });
  };

  const handleClear = () => {
    setFilters({
      childOib: currentChildOib,
    } as InstitutionFilters);
  };

  const handleStep = () => {
    if (selectedInstitution) {
      const isValid = doValidations();
      if (isValid) {
        dispatch(updateSelectedInstitution(selectedInstitution));
        dispatch(updateRequestType(requestType));

        if (
          selectedInstitutionState &&
          selectedInstitutionState.pedagogicalYearInstitutionId !==
            selectedInstitution.pedagogicalYearInstitutionId
        ) {
          dispatch(updateRequestInstitutionPrograms([]));
        }
      } else {
        return isValid;
      }
    } else {
      return false;
    }
  };

  return (
    <>
      <Row className={style.infoMessageRow}>
        <Col md={12}>
          {requestStatusId === RequestStatusEnum.Unlocked ? (
            <InfoPanel
              message={t("institutionDataStep.unlockedRequestWarning")}
              color="warning"
              fullWidth
            />
          ) : (
            <InfoPanel
              message={t("institutionDataStep.mainInfoMessage")}
              color="success"
              fullWidth
            />
          )}
        </Col>
        {errors ? (
          <Col md={12}>
            <InfoPanel
              message={t("validations.institution")}
              color="danger"
              fullWidth
            />
          </Col>
        ) : null}
      </Row>
      {selectedInstitution &&
      selectedInstitution.pedagogicalYearInstitutionId != null ? (
        <>
          <Row>
            <Col md={12}>
              <Typography variant="smallHeading" component="h2">
                {t("institutionDataStep.chosenInstitutionInfo")}
              </Typography>
            </Col>
          </Row>
          <div
            className={style.selectedInstitutionInfo}
            onClick={handleOpenSelectedDetails}
          >
            <IconWrapper color="primary">
              <InstitutionIcon
                aria-label={t("accessibility.institutionIcon")}
              />
            </IconWrapper>

            <Typography variant="mediumBold" component="h3" color="primary">
              {getInstitutionDisplayNameAndTown(
                selectedInstitution.name,
                selectedInstitution.town
              )}
            </Typography>
          </div>
        </>
      ) : (
        <></>
      )}

      {requestStatusId !== RequestStatusEnum.Unlocked && (
        <>
          <InstitutionSearchFilters
            handleSearch={handleSearch}
            filters={filters}
            setFilters={setFilters}
            handleClear={handleClear}
          ></InstitutionSearchFilters>

          <div>
            <Table {...tableProps} />{" "}
          </div>
        </>
      )}
      <InstitutionDetailsModal
        institutionDetails={institutionDetails}
        detailsModalOpen={detailsModalOpen}
        handlerCloseDetails={handlerCloseDetails}
      ></InstitutionDetailsModal>
    </>
  );
}
